import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Download } from 'lucide-react';
import { ThreatSummary } from './ThreatSummary';
import { TTPList } from './TTPList';
import { RelatedIOCs } from './RelatedIOCs';
import { VirusTotalAnalysis } from './VirusTotalAnalysis';
import { SoftwareList } from './SoftwareList';
import { YaraRulesList } from './YaraRulesList';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { generatePDF } from '@/lib/utils/pdfGenerator';
import { SandboxResults } from './SandboxResults';
import { MalwareIntelligence } from './MalwareIntelligence';
import { FloatingNav } from './FloatingNav';
import type { SearchResult } from '@/lib/types/search';
import type { SandboxAnalysis } from '@/lib/types/sandbox';
import type { MalwareIntelligence as MalwareData } from '@/lib/types/malware';

interface SearchResultsProps {
  groups?: SearchResult[];
  sandboxResults?: SandboxAnalysis[];
  malwareResults?: MalwareData[];
  isLoading: boolean;
  hasSearched: boolean;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-8 w-96" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="h-[400px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    </div>
  );
}

export function SearchResults({ 
  groups, 
  sandboxResults, 
  malwareResults, 
  isLoading, 
  hasSearched 
}: SearchResultsProps) {
  const [searchParams] = useSearchParams();
  const [navItems, setNavItems] = useState<Array<{ id: string; label: string; type?: 'group' | 'malware' | 'sandbox' }>>([]);
  
  // Force eager rendering for all sections to avoid lazy loading issues
  const [, setRenderAll] = useState(false);

  // Build navigation items based on available sections
  useEffect(() => {
    console.log('Building nav items with:', {
      groups: groups?.length,
      malwareResults,
      sandboxResults
    });

    const items: Array<{ id: string; label: string; type?: 'group' | 'malware' | 'sandbox' }> = [];
    
    // Create individual items for each threat actor group
    if (groups && groups.length > 0) {
      groups.forEach((group, index) => {
        items.push({
          id: `group-${group.group_id || index}`,
          label: group.common_name || `Threat Actor ${index + 1}`,
          type: 'group' as const
        });
      });
    }
    
    if (malwareResults && malwareResults.length > 0) {
      items.push({
        id: 'malware-section',
        label: 'Malware Intelligence',
        type: 'malware' as const
      });
    }
    
    if (sandboxResults && sandboxResults.length > 0) {
      items.push({
        id: 'sandbox-section',
        label: 'Sandbox Analysis',
        type: 'sandbox' as const
      });
    }
    
    setNavItems(items);
    
    // Enable eager rendering if we have multiple sections that might need navigation
    if (items.length > 1) {
      setRenderAll(true);
    }
  }, [groups, sandboxResults, malwareResults]);

  if (isLoading) {
    return (
      <div className="flex h-[200px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show no results message when search is performed but no results found
  if (hasSearched && (!groups?.length && !sandboxResults?.length && !malwareResults?.length)) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6 text-center">
          <div className="text-muted-foreground">
            No results found. Try adjusting your search terms.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Don't render anything if no search has been performed
  if (!hasSearched && (!groups?.length && !sandboxResults?.length && !malwareResults?.length)) {
    return null;
  }

  const handleExport = async () => {
    if (groups) {
      await generatePDF(groups);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      <FloatingNav items={navItems} />

      {groups && groups.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-ibm-condensed">Pax AI Attribution</h2>
            <Button onClick={handleExport} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export Report
            </Button>
          </div>

          {groups.map((group, index) => (
            <div 
              key={group.group_id} 
              id={`group-${group.group_id || index}`}
              className="space-y-6 pt-6"
            >
              {index > 0 && <hr className="mb-8 border-border" />}
          
              {group.isLoading ? (
                <LoadingSkeleton />
              ) : (
                <>
                  <ThreatSummary
                    name={group.common_name}
                    mitreId={group.group_id}
                    mitreUrl={group.link}
                    coutry={group.coutry}
                    year={group.year}
                    targets={[]}
                    aliases={[]}
                    enrichment={[]}
                    description={group.description}
                    known_aliases={group.known_aliases}
                  />
                  <div className={`grid gap-6 ${group.ttps.length > 0 && group.software.length > 0 ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                    {group.ttps.length > 0 && (
                      <TTPList ttps={group.ttps} />
                    )}
                    {group.software.length > 0 && (
                      <SoftwareList software={group.software} />
                    )}
                  </div>
                  {group.yaraRules.length > 0 && (
                    <YaraRulesList yaraRules={group.yaraRules} />
                  )}
                  <RelatedIOCs groupId={group.group_id} />
                </>
              )}
            </div>
          ))}
        </>
      )}

      {searchParams.get('q')?.match(/^[a-fA-F0-9]{32,64}$/) && (
        <VirusTotalAnalysis hash={searchParams.get('q')!} />
      )}

      {malwareResults && malwareResults.length > 0 && (
        <div id="malware-section">
          <MalwareIntelligence data={malwareResults} />
        </div>
      )}

      {sandboxResults && sandboxResults.length > 0 && (
        <div id="sandbox-section">
          <div className="mt-12 mb-6">
            <h2 className="text-2xl font-ibm-condensed">Sandbox Analysis</h2>
          </div>
          <SandboxResults results={sandboxResults} />
        </div>
      )}
    </div>
  );
}